import { format } from 'date-fns';
import { AlertCircle, Loader2, Receipt, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  type StudentSubscriptionInvoice,
  useGetStudentSubscriptionInvoices,
} from '~entities/student/api/get-student-subscription-invoices';
import { useRecordManualPaymentMutation } from '~entities/student/api/record-manual-payment';

import { useGetCurrency } from '~shared/api/currency-api';
import { formatDateOnlySafe } from '~shared/helpers/datetime';
import { Alert, AlertDescription } from '~shared/shadcn/ui/alert';
import { Button } from '~shared/shadcn/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '~shared/shadcn/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~shared/shadcn/ui/dialog';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import { RadioGroup, RadioGroupItem } from '~shared/shadcn/ui/radio-group';
import { Textarea } from '~shared/shadcn/ui/textarea';

interface RecordCashCheckPaymentProps {
  studentId: string;
  parentId: string;
}

const todayForDateInput = () => format(new Date(), 'yyyy-MM-dd');

/** ENR-4013: date-only dueDate — use UTC calendar day, not viewer-local. */
const formatInvoiceDate = (value?: string | null) =>
  formatDateOnlySafe(value, 'MMM d, yyyy', 'N/A');

const getInvoiceLabel = (
  invoice: Omit<StudentSubscriptionInvoice, 'productName'> & {
    productName?: string | null;
  },
) =>
  (invoice.productName ?? '').trim() || `Invoice ${invoice.invoiceId}`;

export const RecordCashCheckPayment = ({
  studentId,
  parentId,
}: RecordCashCheckPaymentProps) => {
  const [open, setOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'check'>('cash');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(todayForDateInput());
  const [note, setNote] = useState('');
  const [checkNumber, setCheckNumber] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const { data: currencyDetails } = useGetCurrency();
  const { data = [], refetch, isFetching } = useGetStudentSubscriptionInvoices({
    studentId,
  });

  const {
    mutateAsync: recordManualPayment,
    isPending: isRecordingManualPayment,
  } = useRecordManualPaymentMutation();

  const selectedInvoice = data.find(
    (invoice) => invoice.invoiceId === selectedInvoiceId,
  );
  const amount = Number.parseFloat(paymentAmount || '0') || 0;
  const remainingBalance = selectedInvoice?.outstandingAmount ?? 0;
  const overpaymentAmount = Math.max(0, amount - remainingBalance);

  const filteredInvoices = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (!normalizedQuery) return data;

    return data.filter((invoice) => {
      return (
        invoice.invoiceId.toLowerCase().includes(normalizedQuery) ||
        getInvoiceLabel(invoice).toLowerCase().includes(normalizedQuery) ||
        invoice.status.toLowerCase().includes(normalizedQuery)
      );
    });
  }, [data, searchQuery]);

  const resetForm = () => {
    setPaymentMethod('cash');
    setSelectedInvoiceId('');
    setPaymentAmount('');
    setPaymentDate(todayForDateInput());
    setNote('');
    setCheckNumber('');
    setSearchQuery('');
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!/^(?!^\.$)\d*\.?\d{0,2}$/.test(e.target.value)) return;
    setPaymentAmount(e.target.value);
  };

  const handleSubmit = async () => {
    if (!selectedInvoice) {
      toast.error('Select an invoice before recording payment.');
      return;
    }

    try {
      const response = await recordManualPayment({
        studentId,
        parentId,
        invoiceId: selectedInvoice.invoiceId,
        paymentAmount: amount,
        paymentDate,
        paymentMethod,
        checkNumber:
          paymentMethod === 'check' ? checkNumber.trim() : undefined,
        note: note.trim() || undefined,
      });

      if (response.status === 'success') {
        toast.success('Payment recorded successfully.');
        setOpen(false);
        resetForm();
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error('Failed to record payment');
    }
  };

  const submitDisabled =
    !selectedInvoiceId ||
    !paymentDate ||
    amount <= 0 ||
    (paymentMethod === 'check' && !checkNumber.trim()) ||
    isRecordingManualPayment;

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen) {
          resetForm();
          void refetch();
        }
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant='secondary'
          size='sm'
          className='shrink-0'
          title='Record Cash / Check Payment'
          aria-label='Record Cash / Check Payment'
        >
          <Receipt className='size-4' />
          <span className='sr-only md:not-sr-only md:whitespace-nowrap'>
            Record Cash / Check Payment
          </span>
        </Button>
      </DialogTrigger>
      <DialogContent className='max-h-[85vh] overflow-auto sm:max-w-[680px]'>
        <DialogHeader>
          <DialogTitle>Record Cash or Check Payment</DialogTitle>
          <DialogDescription>
            Record an offline payment and apply it to a student invoice.
          </DialogDescription>
        </DialogHeader>

        <div className='grid gap-4 py-4'>
          <div className='grid gap-2'>
            <Label>Invoice</Label>
            <div className='relative'>
              <Search className='absolute left-2.5 top-2.5 size-4 text-muted-foreground' />
              <Input
                placeholder='Search by invoice, product, or status'
                className='pl-8'
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
              />
            </div>
            <RadioGroup
              value={selectedInvoiceId}
              onValueChange={setSelectedInvoiceId}
              className='max-h-[250px] overflow-y-auto rounded-md border'
            >
              {isFetching ? (
                <div className='flex items-center justify-center gap-2 p-4 text-muted-foreground'>
                  <Loader2 className='size-4 animate-spin' />
                  Loading invoices
                </div>
              ) : filteredInvoices.length > 0 ? (
                filteredInvoices.map((invoice) => (
                  <Label
                    key={invoice.invoiceId}
                    htmlFor={`invoice-${invoice.invoiceId}`}
                    className='flex cursor-pointer gap-3 border-b p-3 last:border-b-0 hover:bg-muted/50'
                  >
                    <RadioGroupItem
                      id={`invoice-${invoice.invoiceId}`}
                      value={invoice.invoiceId}
                      className='mt-1'
                    />
                    <div className='grid flex-1 gap-2'>
                      <div className='flex flex-wrap items-center justify-between gap-2'>
                        <div className='font-medium text-primary'>
                          #{invoice.invoiceId}
                        </div>
                        <div className='text-sm capitalize text-muted-foreground'>
                          {invoice.status}
                        </div>
                      </div>
                      <div>{getInvoiceLabel(invoice)}</div>
                      <div className='grid gap-1 text-sm text-muted-foreground sm:grid-cols-3'>
                        <span>
                          Due:{' '}
                          {formatInvoiceDate(invoice.dueDate ?? invoice.invoiceDate)}
                        </span>
                        <span>
                          Total: {currencyDetails?.symbol ?? '$'}
                          {Number(invoice.totalAmount).toFixed(2)}
                        </span>
                        <span>
                          Remaining: {currencyDetails?.symbol ?? '$'}
                          {Number(invoice.outstandingAmount).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </Label>
                ))
              ) : (
                <div className='flex items-center justify-center p-4 text-muted-foreground'>
                  No eligible invoices found
                </div>
              )}
            </RadioGroup>
          </div>

          <div className='grid gap-2'>
            <Label>Payment Method</Label>
            <RadioGroup
              value={paymentMethod}
              className='flex gap-4'
              onValueChange={(value: 'cash' | 'check') => {
                setPaymentMethod(value);
              }}
            >
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value='cash' id='cash' />
                <Label htmlFor='cash' className='cursor-pointer'>
                  Cash
                </Label>
              </div>
              <div className='flex items-center space-x-2'>
                <RadioGroupItem value='check' id='check' />
                <Label htmlFor='check' className='cursor-pointer'>
                  Check
                </Label>
              </div>
            </RadioGroup>
          </div>

          {paymentMethod === 'check' && (
            <div className='grid gap-2'>
              <Label htmlFor='check-number'>Check Number</Label>
              <Input
                id='check-number'
                placeholder='Enter check number'
                value={checkNumber}
                onChange={(e) => {
                  setCheckNumber(e.target.value);
                }}
              />
            </div>
          )}

          <div className='grid gap-4 sm:grid-cols-2'>
            <div className='grid gap-2'>
              <Label htmlFor='payment-amount'>Payment Amount</Label>
              <div className='relative'>
                <span className='absolute left-4 top-1.5 size-4 text-muted-foreground'>
                  {currencyDetails?.symbol ?? '$'}
                </span>
                <Input
                  id='payment-amount'
                  inputMode='decimal'
                  placeholder='Enter payment amount'
                  className='pl-8'
                  value={paymentAmount}
                  onChange={handleAmountChange}
                />
              </div>
            </div>

            <div className='grid gap-2'>
              <Label htmlFor='payment-date'>Payment Date</Label>
              <Input
                id='payment-date'
                type='date'
                value={paymentDate}
                onChange={(e) => {
                  setPaymentDate(e.target.value);
                }}
              />
            </div>
          </div>

          <div className='grid gap-2'>
            <Label htmlFor='payment-note'>Notes / Reference</Label>
            <Textarea
              id='payment-note'
              placeholder='Optional memo or reference'
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
              }}
            />
          </div>

          <Card className='mt-2'>
            <CardHeader className='pb-2'>
              <CardTitle className='text-sm'>Payment Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <div className='space-y-1 text-sm'>
                <div className='flex justify-between'>
                  <span>Invoice Remaining Balance:</span>
                  <span className='font-medium'>
                    {currencyDetails?.symbol ?? '$'}
                    {remainingBalance.toFixed(2)}
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span>Payment Amount:</span>
                  <span className='font-medium'>
                    {currencyDetails?.symbol ?? '$'}
                    {amount.toFixed(2)}
                  </span>
                </div>
                <div className='flex justify-between'>
                  <span>Wallet Credit From Overpayment:</span>
                  <span className='font-medium'>
                    {currencyDetails?.symbol ?? '$'}
                    {overpaymentAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {overpaymentAmount > 0 && (
            <Alert className='border-amber-200 bg-amber-50 text-amber-800'>
              <AlertCircle className='size-4' />
              <AlertDescription>
                {currencyDetails?.symbol ?? '$'}
                {overpaymentAmount.toFixed(2)} will be added to the parent
                wallet as excess credit.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant='outline'>Cancel</Button>
          </DialogClose>
          <Button onClick={handleSubmit} disabled={submitDisabled}>
            {isRecordingManualPayment && <Loader2 className='animate-spin' />}
            Submit Payment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
